import React, { useId } from 'react';
import { Field, Segmented } from '../kit';
import ScopePicker from './ScopePicker';
import TagInput from './TagInput';
import { KINDS, LIMITS } from './skillsLogic';

/**
 * Editor body for a skill (the detail pane swaps to this in edit mode and for
 * "+ New skill"). Controlled: the pane owns the draft, dirty tracking and save.
 */
export default function SkillEditor({ draft, onChange, errors = {}, lookups, disabled, isNew }) {
  const uid = useId();
  const id = (k) => `${uid}-${k}`;
  const set = (k) => (v) => onChange({ [k]: v });
  const bodyLen = (draft.body || '').length;

  return (
    <div className="sk-editor">
      {isNew ? (
        <p className="sk-editor-note">
          New skills start as <strong>proposed</strong>. Approve it from the list once you have reviewed it.
        </p>
      ) : null}
      <Field label="Title" required htmlFor={id('title')} error={errors.title}>
        <input
          id={id('title')}
          className="ak-input"
          value={draft.title}
          maxLength={LIMITS.title}
          disabled={disabled}
          aria-invalid={errors.title ? 'true' : undefined}
          placeholder="Short imperative title, e.g. Budget vs actual: use the dedicated tool first"
          onChange={(e) => set('title')(e.target.value)}
          autoFocus={isNew}
        />
      </Field>
      <Field label="Kind" help={KINDS.find((k) => k.value === draft.kind)?.hint}>
        <Segmented
          mode="choice"
          label="Kind"
          options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
          value={draft.kind}
          onChange={set('kind')}
          disabled={disabled}
        />
      </Field>
      <Field label="Summary" htmlFor={id('summary')} help="One sentence: when the assistant should use this." error={errors.summary}>
        <input
          id={id('summary')}
          className="ak-input"
          value={draft.summary}
          maxLength={LIMITS.summary}
          disabled={disabled}
          onChange={(e) => set('summary')(e.target.value)}
        />
      </Field>
      <Field
        label="Playbook"
        required
        htmlFor={id('body')}
        error={errors.body}
        help={`What the assistant reads: numbered steps, exact tool names and arguments, pitfalls. ${bodyLen.toLocaleString()} / ${LIMITS.body.toLocaleString()}`}
      >
        <textarea
          id={id('body')}
          className="ak-textarea ak-mono sk-body-input"
          value={draft.body}
          rows={12}
          disabled={disabled}
          aria-invalid={errors.body ? 'true' : undefined}
          onChange={(e) => set('body')(e.target.value)}
        />
      </Field>
      <Field label="Scope" error={errors.scope} help="Which conversations the assistant may use this in.">
        <ScopePicker draft={draft} lookups={lookups} disabled={disabled} onChange={onChange} />
      </Field>
      <div className="ak-grid ak-2">
        <Field label="Trigger phrases" htmlFor={id('phrases')} help="Short things a user might say — the strongest match signal. Enter or comma to add.">
          <TagInput id={id('phrases')} label="Trigger phrases" value={draft.phrases} onChange={set('phrases')} placeholder="budget review" />
        </Field>
        <Field label="Intents" htmlFor={id('intents')} help="snake_case labels; they match when every word appears in the question.">
          <TagInput id={id('intents')} label="Intents" mono value={draft.intents} onChange={set('intents')} placeholder="budget_review" />
        </Field>
        <Field label="Tools" htmlFor={id('tools')} help="Tools the playbook involves.">
          <TagInput id={id('tools')} label="Tools" mono value={draft.tools} onChange={set('tools')} placeholder="get_budget_vs_actual" />
        </Field>
        <Field label="Workflows" htmlFor={id('workflows')} help="Workflow keys, if any.">
          <TagInput id={id('workflows')} label="Workflows" mono value={draft.workflows} onChange={set('workflows')} placeholder="budget_review" />
        </Field>
      </div>
    </div>
  );
}
